/**
 * LEMO - Chat Admin ↔ Restaurants
 * Interface de messagerie pour les administrateurs avec catégories
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Send, 
  Paperclip, 
  Image as ImageIcon, 
  File, 
  X, 
  Loader2,
  MessageCircle,
  Inbox,
  Clock,
  CheckCircle,
  ChevronRight,
  ArrowLeft,
  Bell,
  Plus,
  Search
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import { getFullImageUrl } from '../../utils/images';

// Son de notification
const playNotificationSound = () => {
  try {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2JkI2Coverage6Ojk5OTo+PDw8Pz09PT4+Pj4+Pj09PTw8PDs7Ozs6Ojo5OTk4ODg4NzNTBQUFAwMDAQEBAAAAAAAAAAAAAAAAAAAA');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

// Configuration des catégories
const CATEGORIES = [
  { id: 'nouveau', label: 'Nouveau', icon: Inbox, color: 'bg-blue-500', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
  { id: 'en_cours', label: 'En cours', icon: Clock, color: 'bg-amber-500', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
  { id: 'finalise', label: 'Finalisé', icon: CheckCircle, color: 'bg-emerald-500', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' }
];

export default function AdminChat() {
  useAuth();
  const [activeCategory, setActiveCategory] = useState('nouveau');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [stats, setStats] = useState({ counts: {}, unread: {} });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [showNewConvModal, setShowNewConvModal] = useState(false);
  const [restaurantsWithoutConv, setRestaurantsWithoutConv] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastUnreadCountRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Charger les restaurants sans conversation
  const loadRestaurantsWithoutConv = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/restaurants-without-conversation`);
      setRestaurantsWithoutConv(response.data);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    }
  }, []);

  // Démarrer une conversation avec un restaurant
  const startConversation = async (restaurantId) => {
    try {
      const response = await axios.post(`${API}/chat/conversations/start/${restaurantId}`, {});
      toast.success('Conversation créée');
      setShowNewConvModal(false);
      setActiveCategory('nouveau');
      await loadStats();
      await loadConversations('nouveau');
      // Sélectionner la nouvelle conversation
      const restaurant = restaurantsWithoutConv.find(r => r.id === restaurantId);
      setSelectedConversation({
        ...response.data,
        restaurant_name: restaurant?.name || 'Restaurant',
        restaurant_logo: restaurant?.logo
      });
      setMessages([]);
      loadRestaurantsWithoutConv();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  // Charger les statistiques
  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations/stats`);
      
      // Vérifier s'il y a de nouveaux messages non lus
      const newUnreadCount = response.data.total_unread;
      if (newUnreadCount > lastUnreadCountRef.current && lastUnreadCountRef.current > 0) {
        playNotificationSound();
        toast.info('Nouveau message reçu !', { duration: 3000 });
      }
      lastUnreadCountRef.current = newUnreadCount;
      
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Charger les conversations
  const loadConversations = useCallback(async (category = activeCategory) => {
    try {
      const response = await axios.get(`${API}/chat/conversations?category=${category}`);
      setConversations(response.data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, [activeCategory]);

  // Charger les messages
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    try {
      const response = await axios.get(`${API}/chat/conversations/${conversationId}/messages`);
      setMessages(response.data);
      
      // Recharger les stats pour mettre à jour les badges
      loadStats();
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [loadStats]);

  // Initialisation
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadStats();
      await loadConversations();
      await loadRestaurantsWithoutConv();
      setLoading(false);
    };
    
    init();
  }, [loadStats, loadConversations, loadRestaurantsWithoutConv]);

  // Recharger les conversations quand la catégorie change
  useEffect(() => {
    loadConversations(activeCategory);
    setSelectedConversation(null);
    setMessages([]);
  }, [activeCategory, loadConversations]);

  // Polling toutes les 5 secondes
  useEffect(() => {
    const interval = setInterval(() => {
      loadStats();
      loadConversations(activeCategory);
      if (selectedConversation) {
        loadMessages(selectedConversation.id);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [activeCategory, selectedConversation, loadStats, loadConversations, loadMessages]);

  // Scroll quand les messages changent
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sélectionner une conversation
  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    await loadMessages(conv.id);
  };

  // Changer la catégorie d'une conversation
  const changeCategory = async (conversationId, newCategory) => {
    try {
      await axios.put(
        `${API}/chat/conversations/${conversationId}/category`,
        { category: newCategory }
      );
      
      toast.success(`Conversation déplacée vers "${CATEGORIES.find(c => c.id === newCategory)?.label}"`);
      
      // Recharger
      await loadStats();
      await loadConversations(activeCategory);
      
      // Si la conversation actuelle est déplacée, la désélectionner
      if (selectedConversation?.id === conversationId && newCategory !== activeCategory) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
  };

  // Upload de fichier
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const isImage = file.type.startsWith('image/');
      setAttachment({
        url: response.data.url,
        type: isImage ? 'image' : 'file',
        name: file.name
      });
      
      toast.success('Fichier prêt à envoyer');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Supprimer la pièce jointe
  const removeAttachment = () => {
    setAttachment(null);
  };

  // Envoyer un message
  const sendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() && !attachment) return;
    if (!selectedConversation) return;

    setSending(true);
    try {
      const payload = {
        content: newMessage.trim(),
        attachment_url: attachment?.url || null,
        attachment_type: attachment?.type || null,
        attachment_name: attachment?.name || null
      };

      await axios.post(
        `${API}/chat/conversations/${selectedConversation.id}/messages`,
        payload
      );

      setNewMessage('');
      setAttachment(null);
      await loadMessages(selectedConversation.id);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-100px)]">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-emerald-500" />
              Messages des restaurants
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {stats.total || 0} conversation(s) • {stats.total_unread || 0} message(s) non lu(s)
            </p>
          </div>
          <Button
            onClick={() => setShowNewConvModal(true)}
            className="bg-emerald-500 hover:bg-emerald-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle conversation
          </Button>
        </div>

        <div className="flex gap-4 h-[calc(100%-80px)]">
          {/* Sidebar - Liste des conversations */}
          <div className="w-80 bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden">
            {/* Onglets catégories */}
            <div className="flex border-b border-gray-200">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const count = stats.counts?.[cat.id] || 0;
                const unread = stats.unread?.[cat.id] || 0;
                
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex-1 py-3 px-2 text-center relative transition-colors ${
                      activeCategory === cat.id
                        ? `${cat.bgColor} ${cat.textColor} border-b-2`
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    style={activeCategory === cat.id ? { borderBottomColor: cat.color.replace('bg-', '') } : {}}
                  >
                    <Icon className="w-4 h-4 mx-auto mb-1" />
                    <span className="text-xs font-medium block">{cat.label}</span>
                    <span className="text-xs">({count})</span>
                    {unread > 0 && (
                      <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unread}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Liste des conversations */}
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucune conversation</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedConversation?.id === conv.id ? 'bg-emerald-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {conv.restaurant_logo ? (
                        <img
                          src={conv.restaurant_logo}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <span className="text-emerald-700 font-semibold">
                            {conv.restaurant_name?.charAt(0) || 'R'}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium text-gray-900 truncate">
                            {conv.restaurant_name}
                          </h3>
                          {conv.unread_count_admin > 0 && (
                            <Badge className="bg-red-500 text-white text-xs">
                              {conv.unread_count_admin}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {conv.last_message || 'Nouvelle conversation'}
                        </p>
                        {conv.last_message_at && (
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(conv.last_message_at).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                      
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Zone de chat */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden">
            {selectedConversation ? (
              <>
                {/* Header conversation */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {selectedConversation.restaurant_logo ? (
                        <img
                          src={selectedConversation.restaurant_logo}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <span className="text-emerald-700 font-semibold">
                            {selectedConversation.restaurant_name?.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <h2 className="font-semibold text-gray-900">
                          {selectedConversation.restaurant_name}
                        </h2>
                        <p className="text-xs text-gray-500">
                          Conversation démarrée le {new Date(selectedConversation.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Boutons de catégorie */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 mr-2">Déplacer vers :</span>
                      {CATEGORIES.filter(c => c.id !== activeCategory).map((cat) => {
                        const Icon = cat.icon;
                        return (
                          <Button
                            key={cat.id}
                            variant="outline"
                            size="sm"
                            onClick={() => changeCategory(selectedConversation.id, cat.id)}
                            className={`${cat.textColor} border-current hover:${cat.bgColor}`}
                          >
                            <Icon className="w-4 h-4 mr-1" />
                            {cat.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Aucun message</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isMe = msg.sender_role === 'Admin';
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                              isMe
                                ? 'bg-emerald-500 text-white rounded-br-md'
                                : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md'
                            }`}
                          >
                            {!isMe && (
                              <p className="text-xs font-medium text-emerald-600 mb-1">
                                {msg.sender_name} (Restaurant)
                              </p>
                            )}
                            
                            {msg.content && <p className="whitespace-pre-wrap">{msg.content}</p>}
                            
                            {msg.attachment_url && (
                              <div className="mt-2">
                                {msg.attachment_type === 'image' ? (
                                  <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                                    <img
                                      src={msg.attachment_url}
                                      alt="Image"
                                      className="max-w-full rounded-lg max-h-60 object-cover"
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={msg.attachment_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 p-2 rounded-lg ${
                                      isMe ? 'bg-emerald-600' : 'bg-gray-100'
                                    }`}
                                  >
                                    <File className="w-4 h-4" />
                                    <span className="text-sm truncate">{msg.attachment_name || 'Fichier'}</span>
                                  </a>
                                )}
                              </div>
                            )}
                            
                            <p className={`text-xs mt-1 ${isMe ? 'text-emerald-100' : 'text-gray-400'}`}>
                              {new Date(msg.created_at).toLocaleString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Zone de saisie */}
                <div className="border-t border-gray-200 p-4 bg-white">
                  {attachment && (
                    <div className="mb-3 p-3 bg-gray-100 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {attachment.type === 'image' ? (
                          <ImageIcon className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <File className="w-5 h-5 text-blue-500" />
                        )}
                        <span className="text-sm text-gray-700 truncate max-w-xs">
                          {attachment.name}
                        </span>
                      </div>
                      <button onClick={removeAttachment} className="p-1 hover:bg-gray-200 rounded-full">
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  )}

                  <form onSubmit={sendMessage} className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="shrink-0"
                    >
                      {uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Paperclip className="w-5 h-5 text-gray-500" />
                      )}
                    </Button>

                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Écrivez votre réponse..."
                      className="flex-1 rounded-full"
                      disabled={sending}
                    />

                    <Button
                      type="submit"
                      disabled={sending || (!newMessage.trim() && !attachment)}
                      className="bg-emerald-500 hover:bg-emerald-600 rounded-full shrink-0"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">Sélectionnez une conversation</p>
                  <p className="text-sm mt-1">Choisissez un restaurant dans la liste pour voir les messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal nouvelle conversation */}
      <Dialog open={showNewConvModal} onOpenChange={setShowNewConvModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle conversation</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un restaurant..."
                className="pl-10 rounded-xl"
              />
            </div>
            
            {restaurantsWithoutConv.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                Tous les restaurants ont déjà une conversation
              </p>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-2">
                {restaurantsWithoutConv
                  .filter(r => r.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(restaurant => (
                    <button
                      key={restaurant.id}
                      onClick={() => startConversation(restaurant.id)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 border border-gray-200 transition-colors"
                    >
                      {restaurant.logo ? (
                        <img
                          src={getFullImageUrl(restaurant.logo)}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                          <span className="text-emerald-700 font-semibold">
                            {restaurant.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{restaurant.name}</p>
                        <p className="text-xs text-gray-500">Cliquez pour démarrer une conversation</p>
                      </div>
                    </button>
                  ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
